import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  createAccount,
  createAccountFromEmail,
  loginUser,
  validateRecaptcha,
} from "../../api/auth";
import LegacyAuthLogo from "../../components/logo/LegacyAuthLogo";
import { pgAuthCreateAccount, pgAuthLogin } from "../../components/mixPanelEvents/mixPanelEvents";

declare global {
  interface Window {
    grecaptcha?: any;
    recaptchaCallback?: (token: string) => void;
  }
}

const SCORE_SITE_KEY = "6LdUBKopAAAAAKMqTu1HgK0LPZ1lldri-y8wA9PB";
const CHECKBOX_SITE_KEY = "6LeDJNEpAAAAAKAB3pPzM-yhXk9hyNIAzkMGsRev";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getUtmMedium = (): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get("utm_medium") || "";
};

const shouldBypassRecaptcha = (): boolean => {
  const localHosts = ["localhost", "127.0.0.1", "0.0.0.0"];
  const hostname = window.location.hostname;
  const isLocalHost = localHosts.includes(hostname) || hostname.endsWith(".local");
  return isLocalHost || process.env.REACT_APP_DISABLE_RECAPTCHA === "true";
};

type PgInputProps = {
  id: string;
  name: string;
  type: "email" | "password" | "text";
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  autoComplete?: string;
  minLength?: number;
};

const PgInput = ({
  id,
  name,
  type,
  label,
  value,
  onChange,
  className = "",
  autoComplete = "",
  minLength,
}: PgInputProps) => (
  <div className={`pg-input ${type === "password" ? "pg-input-has-eye" : ""} ${className}`.trim()}>
    <input
      id={id}
      type={type}
      name={name}
      value={value}
      required
      autoComplete={autoComplete}
      minLength={minLength}
      onChange={(event) => onChange(event.target.value)}
    />
    <label htmlFor={id} className="pg-input-label">
      {label}
    </label>
    <div className="pg-input-error text-end" />
    {type === "password" && (
      <div className="pg-input-eye">
        <svg width="22" height="15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.09 15c4.823 0 8.574-3.875 10.181-6.125.67-.875.67-2 0-2.875-1.607-2.125-5.358-6-10.18-6C6.268 0 2.518 3.875.91 6.125c-.67.875-.67 2 0 2.75C2.518 11.125 6.268 15 11.09 15zm0-11.25c2.278 0 4.02 1.625 4.02 3.75s-1.742 3.75-4.02 3.75c-2.277 0-4.018-1.625-4.018-3.75s1.741-3.75 4.019-3.75z" fill="#fff"></path>
        </svg>
      </div>
    )}
  </div>
);

const CreateAccountPage = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const emailFromLink = query.get("email") || "";
  const keyFromLink = query.get("key") || "";
  const isEmailKeyVariant = Boolean(emailFromLink && keyFromLink);

  const [username, setUsername] = useState(emailFromLink);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCheckboxRecaptcha, setShowCheckboxRecaptcha] = useState(false);
  const bypassRecaptcha = shouldBypassRecaptcha();

  const isDefaultValid = EMAIL_REGEX.test(username.trim()) && password.length >= 8;
  const isFromEmailValid = password2.length >= 8;

  const continueDefaultFlow = useCallback(async (captchaChecked = false): Promise<void> => {
    if (!isDefaultValid) {
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (!captchaChecked && !bypassRecaptcha) {
        if (!window.grecaptcha?.enterprise) {
          setErrorMessage("Failed to verify CAPTCHA");
          return;
        }

        const token = await window.grecaptcha.enterprise.execute(SCORE_SITE_KEY, { action: "create_account" });
        const captchaResult = await validateRecaptcha({ token, method: "score" });
        if (!captchaResult.success) {
          setShowCheckboxRecaptcha(true);
          return;
        }
      }

      const campaign = getUtmMedium();
      const createResponse = await createAccount({
        username: username.trim(),
        password,
        campaign,
      });

      if (!createResponse.success) {
        setErrorMessage(createResponse.message);
        return;
      }

      pgAuthCreateAccount("Email", username.trim());
      const loginResponse = await loginUser({
        username: username.trim(),
        password,
        remember: false,
      });

      if (!loginResponse.success) {
        setErrorMessage(loginResponse.message || "Email is already in use");
        return;
      }

      pgAuthLogin();
      localStorage.removeItem("hideAppBar");
      window.location.href = window.location.origin;
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [bypassRecaptcha, isDefaultValid, password, username]);

  useEffect(() => {
    if (isEmailKeyVariant || bypassRecaptcha) {
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SCORE_SITE_KEY}`;
    script.async = true;
    document.body.appendChild(script);

    const checkboxScript = document.createElement("script");
    checkboxScript.src = "https://www.google.com/recaptcha/enterprise.js";
    checkboxScript.async = true;
    checkboxScript.defer = true;
    document.body.appendChild(checkboxScript);

    window.recaptchaCallback = async (token: string) => {
      try {
        const response = await validateRecaptcha({ token, method: "checkbox" });
        if (!response.success) {
          setErrorMessage("Failed to verify CAPTCHA");
          return;
        }
        await continueDefaultFlow(true);
      } catch (error) {
        setErrorMessage("Failed to verify CAPTCHA");
      }
    };

    return () => {
      window.recaptchaCallback = undefined;
      if (script.parentElement) {
        script.parentElement.removeChild(script);
      }
      if (checkboxScript.parentElement) {
        checkboxScript.parentElement.removeChild(checkboxScript);
      }
    };
  }, [bypassRecaptcha, continueDefaultFlow, isEmailKeyVariant]);

  const submitFromEmailVariant = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!isFromEmailValid) {
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await createAccountFromEmail({
        email: emailFromLink,
        key: keyFromLink,
        password: password2,
      });

      if (!response.success) {
        setErrorMessage(response.message);
        return;
      }

      pgAuthCreateAccount("Email", emailFromLink);
      setSuccessMessage(response.message);
      window.location.reload();
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitDefaultVariant = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    await continueDefaultFlow();
  };

  return (
    <div className="pg-users-forms-screen">
      <div className="pg-users-forms-screen-content">
        <div className="pg-users-forms-screen-sidebar registration-sidebar">
          <LegacyAuthLogo fill="#fff" href={window.location.origin} />
          <div className="pg-users-forms-screen-sidebar-quote">
            <span className="pg-users-forms-screen-sidebar-quote-huge-text">“The best all-in-one golf training system”</span>
            <span className="pg-users-forms-screen-sidebar-quote-big-text">Martin Chuck</span>
            <span className="pg-users-forms-screen-sidebar-quote-small-text">Golf Digest Top 10 Instructor</span>
          </div>
        </div>
        <div className="pg-users-forms-screen-main-content">
          <div className="pg-users-forms-screen-main-content-wrapper">
            <div className="pg-users-forms-screen-form">
              {isEmailKeyVariant ? (
                <>
                  <div className="logo-mobile">
                    <LegacyAuthLogo fill="#000" href={window.location.origin} />
                  </div>
                  <h1 className="fs-32 fw-700 text-left mb-40">Create Account</h1>
                  <p className="text-center mb-25">Username: {emailFromLink}</p>
                  <form className="d-inline-block w-100" onSubmit={submitFromEmailVariant}>
                    <PgInput
                      id="pg-input-password"
                      name="password"
                      type="password"
                      label="Choose Password *"
                      value={password2}
                      onChange={setPassword2}
                      minLength={8}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary d-block w-100"
                      disabled={!isFromEmailValid || loading}
                    >
                      Create Account
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div className="logo-mobile">
                    <LegacyAuthLogo fill="#000" href={window.location.origin} />
                  </div>
                  <h1 className="fs-32 fw-700 text-left mb-40">Create Account</h1>
                  <form className="w-100" onSubmit={submitDefaultVariant}>
                    <PgInput
                      id="pg-input-username"
                      name="username"
                      type="email"
                      label="Username/Email"
                      value={username}
                      onChange={setUsername}
                      className="mb-24"
                      autoComplete="username"
                    />
                    <PgInput
                      id="pg-input-password"
                      name="password"
                      type="password"
                      label="Password"
                      value={password}
                      onChange={setPassword}
                      className="mb-24"
                      minLength={8}
                    />
                    <button
                      type="submit"
                      className="btn btn-primary d-block w-100"
                      disabled={!isDefaultValid || loading}
                    >
                      Create Account
                    </button>
                  </form>
                  <p className="small-text text-center mb-40">
                    Need help with login?{" "}
                    <a
                      href="https://www.performancegolf.com/contact"
                      className="d-inline-block green-link text-underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Contact Us
                    </a>
                  </p>
                  {showCheckboxRecaptcha && !bypassRecaptcha && (
                    <div id="recaptchaBox" className="mt-15">
                      <div
                        className="g-recaptcha"
                        data-sitekey={CHECKBOX_SITE_KEY}
                        data-action="create_account"
                        data-callback="recaptchaCallback"
                      />
                    </div>
                  )}
                </>
              )}
              {errorMessage && <div className="alert alert-danger mt-10">{errorMessage}</div>}
              {successMessage && <div className="alert alert-success mt-10">{successMessage}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPage;
